import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncData } from '@myhealth-raccoon/services';
import { Button, Spinner } from '@myhealth-raccoon/components';
import { useBeforeUnload } from 'react-use';
import { captureException } from '@sentry/react';
import { toast } from "react-toastify";

import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { useDispatch, useSelector } from 'src/store';
import { PatientDetailsUpdateRequest, patientService } from 'src/services/patient';
import { UpdateEmail } from 'src/components/additionalDetails/UpdateEmail';
import { UpdatePHI } from 'src/components/additionalDetails/UpdatePHI';
import { PreventBackButton } from 'src/components/functional';
import { StickyBottomBar } from 'src/components/StickyBottomBar';
import { ContentContainer } from 'src/components/ContentContainer';
import { ConsentModule, ConsentModuleRef } from 'src/components/additionalDetails/ConsentModule';
import { BasicInfoForm, BasicInfoFormRef } from 'src/components/forms/BasicInfoForm';
import { MedicareForm, MedicareFormRef } from 'src/components/forms/MedicareForm';
import { consentService, EMAIL_CAMPAIGN_CONSENT_ID, SMS_CAMPAIGN_CONSENT_ID } from 'src/services/consent';
import { PHIFormRef, UpdatePHIForm } from 'src/components/forms/UpdatePHIForm';
import { FundCodes } from 'src/utils/fundCodes';
import { setSawAdditionalDetailsScreen } from 'src/store/bookingFlow';

function assert<T>(truthy: T | undefined, errorMessage?: string): truthy is T {
    if (!truthy) {
        throw errorMessage || "Assertion failed";
    }
    return !!truthy;
}

export function AdditionalDetails() {
    useBookingFlowStep(3);
    const navigate = useNavigate();
    // show dialog before closing/reloading
    useBeforeUnload();
    const dispatch = useDispatch();

    const [isEmailDirty, setIsEmailDirty] = useState(false);
    const [isPhiDirty, setIsPhiDirty] = useState(false);
    const [shouldShowValidation, setShouldShowValidation] = useState(false);
    const basicInfoRef = useRef<BasicInfoFormRef>(null);
    const medicareFormRef = useRef<MedicareFormRef>(null);
    const phiFormRef = useRef<PHIFormRef>(null);
    const consentModuleRef = useRef<ConsentModuleRef>(null);

    const siteId = useSelector(state => state.appointment.clinicId);
    const isNewPatient = useSelector(state => state.appointment.isNewPatient);

    const { data: healthInfo, isLoading: loadingHealthInfo, invoke: reloadHealthInfo } = useAsyncData({
        method: patientService.GetPatientHealthInfo,
        service: patientService
    }, siteId);

    const { data: basicDetails, isLoading: loadingBasicDetails, invoke: reloadBasicDetails, ...basicDetailsRequest } = useAsyncData({
        method: patientService.GetBasicDetails,
        service: patientService
    }, siteId);

    const { data: patientHasDetails, isLoading: loadingPatientHasDetails } = useAsyncData({
        service: patientService,
        method: patientService.PatientHasDetails
    }, siteId)

    const onNext = useCallback(async () => {
        if (isEmailDirty || isPhiDirty) {
            toast.error("You have unsaved changes. Please cancel or complete pending updates.");
            setShouldShowValidation(true);
            return;
        }
        try {
            // validate requests
            const [basicInfo, medicareInfo, phiInfo, consentOption] = await Promise.all([
                basicInfoRef.current?.submit(),
                medicareFormRef.current?.submit(),
                phiFormRef.current?.submit(),
                consentModuleRef.current?.submit()
            ]);
            assert(patientHasDetails.gender || basicInfo?.gender);
            assert(patientHasDetails.address || basicInfo?.address);
            assert(patientHasDetails.medicare || medicareInfo);
            const promises: Promise<unknown>[] = [];

            try {
                // send requests
                if (basicInfo || medicareInfo) {
                    const patientUpdate: PatientDetailsUpdateRequest = {};
                    if (!patientHasDetails.gender) {
                        patientUpdate.gender = basicInfo!.gender;
                    }
                    if (!patientHasDetails.address) {
                        patientUpdate.address = basicInfo!.address;
                    }
                    if (!patientHasDetails.medicare && medicareInfo?.haveMedicare === "yes") {
                        const { haveMedicare: _, ...medi } = medicareInfo;
                        patientUpdate.medicare = medi;
                    }
                    promises.push(patientService.UpdatePatientBasicDetails(siteId, patientUpdate));
                }
                if (phiInfo && phiInfo.havePhi === "yes") {
                    promises.push(patientService.UpdatePatientHealthInfo(siteId, {
                        Name: phiInfo.providerId !== 9999 && FundCodes.find(f => phiInfo.providerId === f.FundID)?.FundName || phiInfo.providerName,
                        Number: phiInfo.customerId,
                        ExpiryDate: phiInfo.expiry
                    }));
                }

                if (consentOption) {
                    promises.push(consentService.AddPatientConsent({
                        consentId: EMAIL_CAMPAIGN_CONSENT_ID,
                        consentStatus: consentOption
                    }));
                    promises.push(consentService.AddPatientConsent({
                        consentId: SMS_CAMPAIGN_CONSENT_ID,
                        consentStatus: consentOption
                    }))
                }

                await Promise.all(promises);
                dispatch(setSawAdditionalDetailsScreen(true));
                navigate('/app/consent');
            } catch (promisesException) {
                captureException(promisesException);
                if (promisesException instanceof Error) {
                    toast.error(promisesException.message || promisesException.name);
                }
            }
        } catch (_) {
            setShouldShowValidation(true);
        }

    }, [dispatch, isEmailDirty, isPhiDirty, navigate, patientHasDetails, siteId]);


    if (loadingHealthInfo || loadingPatientHasDetails) {
        return <ContentContainer className='flex-1 lg:flex-none'>
            <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>
                Additional Info</h1>
            <Spinner size='xl' className='flex justify-center' />
        </ContentContainer>
    }

    return <>
        <ContentContainer className='flex-1 lg:flex-none'>
            <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Additional Info</h1>
            <p className='text-base'>Please review and update the details below if there have been any changes in the past 12 months.</p>
            <p className='text-base'><br /></p>
            <p className='text-base'><span className='text-primary'>*</span> indicates a required field</p>

            {patientHasDetails
                && (!patientHasDetails.address || !patientHasDetails.gender) && <BasicInfoForm
                    className="mt-4"
                    ref={basicInfoRef}
                    shouldShowValidation={shouldShowValidation}
                    prepend={<>
                        <h2 className='text-base font-semibold mt-8 mb-1'>
                            Basic Info
                        </h2>
                        <p className='mb-4'>Enter your gender and address.</p>
                    </>}
                    append={<hr className='my-5' />}
                    editAddress={!patientHasDetails.address}
                    editGender={!patientHasDetails.gender}
                />}

            <h2 className='text-base font-semibold mt-8 mb-1'>
                Contact Email <span aria-label='Required' className='text-primary'>*</span>
            </h2>
            <p >
                We'll send your booking confirmation here.
            </p>

            {loadingBasicDetails
                ? <Spinner />
                : basicDetails ?
                    <UpdateEmail
                        className='my-5'
                        currentEmail={basicDetails.email}
                        setIsDirty={setIsEmailDirty}
                        afterSubmit={reloadBasicDetails}
                    />
                    : <pre className='text-error'>{basicDetailsRequest.error.message}</pre>}

            <hr className='my-5' />

            {patientHasDetails
                && !patientHasDetails.medicare && <MedicareForm
                    ref={medicareFormRef}
                    required={isNewPatient}
                    className="mt-4"
                    prepend={<>
                        <h2 className='text-base font-semibold mt-5 mb-1'>
                            Medicare
                        </h2>
                        <p>
                            Providing your Medicare details helps us process prescriptions efficiently. It also ensures you can access cheaper medications under the Pharmaceutical Benefits Scheme (PBS) and reduces any out-of-pocket costs where applicable.
                        </p>
                    </>}
                    append={<hr className='my-5' />}
                    shouldShowValidation={shouldShowValidation}
                />}


            <h2 className='text-base font-semibold mt-5 mb-1'>
                Private Health Insurance
            </h2>

            <p>
                Your Medical Practice may partner with Health Insurance Companies to augment your care, giving you access to free or discounted services.
                <br />
                <br />
                All information is held in line with our <a target="_blank" href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}/privacy-policy/`} className='text-primary'>Privacy Policy</a>.
            </p>

            {isNewPatient
                ? <UpdatePHIForm
                    ref={phiFormRef}
                    canRemovePHI
                    required
                    className='my-5'
                />
                : loadingHealthInfo
                    ? <Spinner />
                    : <UpdatePHI
                        patientHealthInfo={healthInfo}
                        reloadHealthInfo={reloadHealthInfo}
                        className='my-5'
                        setIsDirty={setIsPhiDirty}
                    />
            }
            <ConsentModule
                prepend={<>
                    <hr className='my-5' />
                    <h2 className='text-base font-semibold mt-5'>Your Communication Choices</h2>
                    <p className='mb-5'>
                        We may send you updates regarding health information.
                    </p>
                </>}
                shouldShowValidation={shouldShowValidation}
                ref={consentModuleRef}
            />
        </ContentContainer>
        <StickyBottomBar>
            <Button className='flex-1 text-center' disabled={loadingPatientHasDetails || loadingBasicDetails || loadingHealthInfo} onClick={onNext}>Next</Button>
        </StickyBottomBar>
        <PreventBackButton />

    </>
}